import { createContext, useCallback, useEffect, useMemo, useState } from "react";

/**
 * ======================================================
 * PANEL NOTICE CONTEXT
 * ======================================================
 *
 * Shared notice state for desktop and mobile shells.
 */

const DEFAULT_DISMISS_MS = 2800;

export const PanelNoticeContext = createContext(null);

export function PanelNoticeProvider({ children }) {
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!notice || notice.type === "loading") {
      return undefined;
    }

    const timeoutMs = notice.autoDismissMs ?? DEFAULT_DISMISS_MS;
    const timer = window.setTimeout(() => {
      setNotice(null);
    }, timeoutMs);

    return () => window.clearTimeout(timer);
  }, [notice]);

  const showNotice = useCallback((nextNotice) => {
    if (!nextNotice?.message) {
      return;
    }

    setNotice({
      type: nextNotice.type || "info",
      title: nextNotice.title,
      message: nextNotice.message,
      glyph: nextNotice.glyph,
      tone: nextNotice.tone,
      actionLabel: nextNotice.actionLabel,
      onAction: nextNotice.onAction,
      autoDismissMs: nextNotice.autoDismissMs,
    });
  }, []);

  const clearNotice = useCallback(() => {
    setNotice(null);
  }, []);

  const value = useMemo(
    () => ({
      notice,
      showNotice,
      clearNotice,
    }),
    [notice, showNotice, clearNotice]
  );

  return (
    <PanelNoticeContext.Provider value={value}>
      {children}
    </PanelNoticeContext.Provider>
  );
}
